import { ref } from 'vue';
import { apiUrl } from './api-url';

const COMPANY_LOGO_CACHE = 'eqover-company-assets-v1';
const COMPANY_LOGO_ENDPOINT = apiUrl('/api/public/company-logo');

const companyLogoUrl = ref('');
let companyLogoRequest: Promise<void> | undefined;
let companyLogoObjectUrl = '';

export function useCompanyLogo(): { companyLogoUrl: typeof companyLogoUrl; loadCompanyLogo: () => Promise<void> } {
  return { companyLogoUrl, loadCompanyLogo };
}

function loadCompanyLogo(): Promise<void> {
  if (companyLogoRequest) return companyLogoRequest;
  companyLogoRequest = loadCachedThenRefresh()
    .catch(() => undefined)
    .finally(() => { companyLogoRequest = undefined; });
  return companyLogoRequest;
}

async function loadCachedThenRefresh(): Promise<void> {
  const cache = await openLogoCache();
  const cached = await cache?.match(COMPANY_LOGO_ENDPOINT);
  if (cached) await displayResponse(cached);

  try {
    const response = await fetch(COMPANY_LOGO_ENDPOINT, {
      headers: { Accept: 'image/*' },
      cache: 'no-cache',
    });
    if (response.status === 404) {
      await cache?.delete(COMPANY_LOGO_ENDPOINT);
      clearDisplayedLogo();
      return;
    }
    if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) return;
    await cache?.put(COMPANY_LOGO_ENDPOINT, response.clone());
    await displayResponse(response);
  } catch {
    // El logo en caché sigue disponible cuando la app no tiene conexión.
  }
}

async function openLogoCache(): Promise<Cache | undefined> {
  if (!('caches' in window)) return undefined;
  try { return await caches.open(COMPANY_LOGO_CACHE); }
  catch { return undefined; }
}

async function displayResponse(response: Response): Promise<void> {
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) return;
  const nextUrl = URL.createObjectURL(blob);
  if (companyLogoObjectUrl) URL.revokeObjectURL(companyLogoObjectUrl);
  companyLogoObjectUrl = nextUrl;
  companyLogoUrl.value = nextUrl;
}

function clearDisplayedLogo(): void {
  if (companyLogoObjectUrl) URL.revokeObjectURL(companyLogoObjectUrl);
  companyLogoObjectUrl = '';
  companyLogoUrl.value = '';
}
